/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getDispatchStatisticsActionsLogs as getDispatchStatisticsReportSearchLogs,
  getDispatchStatisticsLogDetails,
} from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { mapRouteToReportTypeLabel } from './reportTypeOptions'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import AuditDispatchStatisticsReportFilters from './AuditDispatchStatisticsReportFilters'
import {
  SearchLogResponse,
  DispatchStatisticsReportSearchLog,
  DispatchStatisticsReportSearchLogRow,
} from '../interfaces'

const limit = 15

// Função para extrair datas do content
const extractDatesFromContent = (content: string) => {
  try {
    const parsedContent = JSON.parse(content)
    return {
      start_date: parsedContent.start_date || null,
      finish_date: parsedContent.finish_date || null,
    }
  } catch {
    return {
      start_date: null,
      finish_date: null,
    }
  }
}

// Função dedicada para mapear os dados das linhas, separando responsabilidade
const mapDispatchStatisticsReportRows = (
  data: readonly DispatchStatisticsReportSearchLog[],
  page: number,
  pageSize: number,
  translate: (key: string) => string
): GridRowsProp => {
  return data.map(
    (element: DispatchStatisticsReportSearchLog, index: number) => {
      const generatedId = element.id ?? page * pageSize + index + 1

      const reportName = element.route
        ? mapRouteToReportTypeLabel(translate, element.route)
        : translate('Unknown Report')

      // action_display agora é derivado apenas da rota (reportName)
      const actionLabel = reportName

      const { start_date: contentStartDate, finish_date: contentFinishDate } =
        element.content
          ? extractDatesFromContent(element.content)
          : { start_date: null, finish_date: null }

      return {
        id: generatedId,
        ...element,
        'user:representation_name': element?.user_name || '',
        report_display: reportName,
        action_display: actionLabel,
        origin: element?.station
          ? `Estação - ${element?.station}`
          : element?.DispatchStatistics
          ? `DispatchStatistics - ${element?.DispatchStatistics}`
          : '',
        content_start_date: contentStartDate,
        content_finish_date: contentFinishDate,
        content_period:
          contentStartDate || contentFinishDate
            ? `${
                contentStartDate
                  ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm')
                  : ''
              }${contentStartDate && contentFinishDate ? ' - ' : ''}${
                contentFinishDate
                  ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm')
                  : ''
              }`
            : null,
      } as DispatchStatisticsReportSearchLogRow
    }
  )
}

export default function ListAllAuditDispatchStatisticsReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<'DispatchStatistics' | 'station' | null>(null)
  const selectedDispatchStatistics = useHookstate<number | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandDispatchStatisticsReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<DispatchStatisticsReportSearchLogRow | null>(null)
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null) // Novo state para loading do row específico
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})

  // mapActionType movida para reportTypeOptions.ts (import acima)
  const columns: readonly GridColDef[] = [
    {
      field: 'user:representation_name',
      headerName: translate('Usuário'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'route',
      headerName: translate('Tipo de Relatório'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (value) =>
        mapRouteToReportTypeLabel(translate, value as string),
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => {
        const rowId = (params.row as DispatchStatisticsReportSearchLogRow).id ?? -1
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Tooltip
              title={translate('Visualizar')}
              open={openTooltips[rowId] ?? false}
              onOpen={() =>
                setOpenTooltips((prev) => ({
                  ...prev,
                  [rowId]: true,
                }))
              }
              onClose={() =>
                setOpenTooltips((prev) => ({
                  ...prev,
                  [rowId]: false,
                }))
              }
            >
              <IconButton
                onClick={() => {
                  setOpenTooltips((prev) => ({
                    ...prev,
                    [rowId]: false,
                  })) // Força o fechamento do tooltip ao clicar
                  handleView(params.row as DispatchStatisticsReportSearchLogRow)
                }}
                size="small"
                aria-label={translate('Visualizar')}
                disabled={loadingRowId === rowId}
              >
                {loadingRowId === rowId ? (
                  <CircularProgress size={20} />
                ) : (
                  <VisibilityIcon />
                )}
              </IconButton>
            </Tooltip>
          </div>
        )
      },
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    action.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandDispatchStatisticsReports.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: SearchLogResponse =
      await getDispatchStatisticsReportSearchLogs({
        user_id: userId.get() || undefined,
        start_date: startDate,
        finish_date: finishDate,
        action: action.get(),
        page: page,
        limit: pageSize,
        orders:
          orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
      }).finally(() => {
        loadingButton.set(false)
        expandDispatchStatisticsReports.set(true)
      })

    // Usar a função dedicada para mapear as linhas
    setRows(
      mapDispatchStatisticsReportRows(
        searchLogData.data,
        page,
        pageSize,
        translate
      )
    )

    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const handleView = async (row: DispatchStatisticsReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }

      const details = await getDispatchStatisticsLogDetails(Number(row.id))
      setSelectedRow(details as DispatchStatisticsReportSearchLogRow)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditDispatchStatisticsReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          originType={originType}
          selectedDevice={selectedDispatchStatistics}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() =>
                expandDispatchStatisticsReports.set(
                  !expandDispatchStatisticsReports.get()
                )
              }
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandDispatchStatisticsReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandDispatchStatisticsReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={(event?: unknown, reason?: string) => {
          if (reason !== 'backdropClick') {
            setOpenModal(false)
          }
        }}
        selectedRow={selectedRow}
        fieldsToDisplay={[
          {
            label: translate('Dispatches'),
            value: selectedRow?.dispatches
              ? selectedRow.dispatches
                  .map((dispatch) => `${dispatch.code}`)
                  .join(', ')
              : 'Não informado',
            size: 12,
          },
        ]}
      />
    </>
  )
}
